import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Dismiss20Regular,
  Save20Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import {
  ApiError,
  getLoanById,
  updateLoan,
  type LoanPayload,
} from "../../emprestimos/emprestimosApi";
import { APP_LOCALE } from "../../config/locale";
import {
  PERIODICITY_OPTIONS,
  frequencyMonthsForLabel,
  labelForFrequencyMonths,
} from "./periodicity";

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para editar empréstimos.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível atualizar o empréstimo.";
};

const parseLoadError = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar empréstimos.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível carregar o empréstimo.";
};

const formatDate = (value?: string | null): string => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleDateString(APP_LOCALE);
};

const toDateInputValue = (value?: string | null): string => {
  if (!value) return "";
  return value.slice(0, 10);
};

const numberToInput = (value?: number | null): string =>
  value !== null && value !== undefined ? String(value) : "";

const inputClass =
  "w-full px-3 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary";
const inputStyle = {
  borderRadius: "var(--radius-button)",
  fontSize: "var(--text-base)",
} as const;
const labelClass = "block text-foreground mb-2";
const labelStyle = {
  fontSize: "var(--text-caption)",
  fontWeight: "var(--font-weight-semi-bold)",
} as const;

export function EditarEmprestimoView() {
  const navigate = useNavigate();
  const { loanId } = useParams();
  const [entidadeFinanceira, setEntidadeFinanceira] = useState("");
  const [descricao, setDescricao] = useState("");
  const [referenciaExtrato, setReferenciaExtrato] = useState("");
  const [periodicidade, setPeriodicidade] = useState("");
  const [estado, setEstado] = useState("");
  const [capital, setCapital] = useState("");
  const [taxaAnual, setTaxaAnual] = useState("");
  const [numeroPrestacoes, setNumeroPrestacoes] = useState("");
  const [primeiraPrestacao, setPrimeiraPrestacao] = useState("");
  const [prestacao, setPrestacao] = useState("");
  const [dataVencimento, setDataVencimento] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [lastUpdatedLabel, setLastUpdatedLabel] = useState<string | null>(null);
  const [loanSnapshotId, setLoanSnapshotId] = useState<number | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadLoan = async () => {
      if (!loanId) {
        setLoadError("Empréstimo inválido.");
        setIsLoading(false);
        return;
      }

      const parsedId = Number(loanId);
      if (Number.isNaN(parsedId)) {
        setLoadError("Empréstimo inválido.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setLoadError(null);
      setSubmitError(null);

      try {
        const response = await getLoanById(parsedId);
        if (!isMounted) return;

        setLoanSnapshotId(response.id);
        setEntidadeFinanceira(response.financialEntity ?? "");
        setDescricao(response.description ?? "");
        setReferenciaExtrato(response.bankReference ?? "");
        // Prefere o label mapeado da frequência; cai no label guardado (legacy).
        setPeriodicidade(
          labelForFrequencyMonths(response.frequencyMonths) ??
            response.periodicity ??
            "",
        );
        setEstado(response.status ?? "");
        setCapital(numberToInput(response.principal));
        setTaxaAnual(numberToInput(response.annualInterestRatePercent));
        setNumeroPrestacoes(numberToInput(response.numberOfInstallments));
        setPrimeiraPrestacao(toDateInputValue(response.firstInstallmentDate));
        setPrestacao(numberToInput(response.installmentValue));
        setDataVencimento(toDateInputValue(response.dueDate));
        setLastUpdatedLabel(formatDate(response.lastModified));
      } catch (error) {
        if (!isMounted) return;
        setLoadError(parseLoadError(error));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadLoan();

    return () => {
      isMounted = false;
    };
  }, [loanId]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitError(null);

    if (!loanSnapshotId) {
      setSubmitError("Empréstimo inválido.");
      return;
    }

    if (!entidadeFinanceira.trim()) {
      setSubmitError("A entidade financeira é obrigatória.");
      return;
    }

    setIsSaving(true);

    const payload: LoanPayload = {
      financialEntity: entidadeFinanceira.trim(),
      description: descricao.trim() || null,
      bankReference: referenciaExtrato.trim() || null,
      periodicity: periodicidade.trim() || null,
      status: estado.trim() || null,
      principal: capital !== "" ? parseFloat(capital) : null,
      annualInterestRatePercent: taxaAnual !== "" ? parseFloat(taxaAnual) : null,
      numberOfInstallments:
        numeroPrestacoes !== "" ? parseInt(numeroPrestacoes, 10) : null,
      firstInstallmentDate: primeiraPrestacao !== "" ? primeiraPrestacao : null,
      frequencyMonths: frequencyMonthsForLabel(periodicidade),
      // Não se envia o nº de prestações pagas: chegam pela reconciliação e pelo registo, e reenviar o
      // valor lido ao abrir desfazia uma prestação paga entretanto.
      installmentValue: prestacao !== "" ? parseFloat(prestacao) : null,
      dueDate: dataVencimento !== "" ? dataVencimento : null,
    };

    try {
      await updateLoan(loanSnapshotId, payload);
    } catch (error) {
      setSubmitError(parseErrorMessage(error));
      setIsSaving(false);
      return;
    }

    setIsSaving(false);
    navigate("/emprestimos", {
      state: { toast: "Empréstimo atualizado com sucesso" },
    });
  };

  // Preserva um label de periodicidade legacy que não corresponda às opções standard.
  const hasCustomPeriodicity =
    periodicidade !== "" &&
    !PERIODICITY_OPTIONS.some((option) => option.label === periodicidade);

  const actions = (
    <div className="flex items-center gap-4">
      <button
        className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{ borderRadius: "var(--radius-button)" }}
        onClick={() => navigate("/emprestimos")}
        disabled={isSaving}
      >
        <Dismiss20Regular className="w-5 h-5" />
        <span className="hidden sm:inline">Cancelar</span>
      </button>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => {
          const form = document.getElementById(
            "editar-emprestimo-form",
          ) as HTMLFormElement | null;
          form?.requestSubmit();
        }}
        disabled={isSaving || isLoading}
      >
        <Save20Regular className="w-5 h-5" />
        {isSaving ? "A guardar..." : "Guardar Alterações"}
      </button>
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Editar Empréstimo"
        subtitle="Atualize os dados do empréstimo."
        actions={actions}
        backButton={{ onClick: () => navigate("/emprestimos") }}
      />

      <div className="flex-1 overflow-y-auto bg-background">
        <div className="p-4 md:p-6">
          {loadError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {loadError}
            </div>
          )}

          {submitError && (
            <div
              className="bg-card border border-[var(--destructive)] px-4 py-3 mb-6 text-[var(--destructive)]"
              style={{ borderRadius: "var(--radius-card)" }}
            >
              {submitError}
            </div>
          )}

          <div
            className="text-secondary-text mb-6"
            style={{
              fontSize: "var(--text-caption)",
              fontWeight: "var(--font-weight-normal)",
            }}
          >
            Última atualização: {lastUpdatedLabel ?? "—"}
          </div>

          <form id="editar-emprestimo-form" onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-20">
              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <h3
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Dados do Empréstimo
                  </h3>

                  <div>
                    <label
                      htmlFor="entidadeFinanceira"
                      className={labelClass}
                      style={labelStyle}
                    >
                      Entidade Financeira{" "}
                      <span className="text-primary">*</span>
                    </label>
                    <input
                      id="entidadeFinanceira"
                      type="text"
                      value={entidadeFinanceira}
                      onChange={(event) =>
                        setEntidadeFinanceira(event.target.value)
                      }
                      className={inputClass}
                      style={inputStyle}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label htmlFor="descricao" className={labelClass} style={labelStyle}>
                      Descrição
                    </label>
                    <input
                      id="descricao"
                      type="text"
                      value={descricao}
                      onChange={(event) => setDescricao(event.target.value)}
                      className={inputClass}
                      style={inputStyle}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label htmlFor="referenciaExtrato" className={labelClass} style={labelStyle}>
                      Referência no extrato
                    </label>
                    <input
                      id="referenciaExtrato"
                      type="text"
                      placeholder="Nº do contrato ou texto do débito no banco"
                      value={referenciaExtrato}
                      onChange={(event) => setReferenciaExtrato(event.target.value)}
                      className={inputClass}
                      style={inputStyle}
                    />
                    <p className="text-xs text-secondary-text mt-1">
                      Com isto, o débito de cada prestação fica pago sozinho na reconciliação bancária, com o valor real.
                    </p>
                  </div>

                  <div>
                    <label htmlFor="periodicidade" className={labelClass} style={labelStyle}>
                      Periodicidade
                    </label>
                    <select
                      id="periodicidade"
                      value={periodicidade}
                      onChange={(event) => setPeriodicidade(event.target.value)}
                      className={inputClass}
                      style={inputStyle}
                      disabled={isLoading}
                    >
                      <option value="">Selecionar…</option>
                      {PERIODICITY_OPTIONS.map((option) => (
                        <option key={option.label} value={option.label}>
                          {option.label}
                        </option>
                      ))}
                      {hasCustomPeriodicity && (
                        <option value={periodicidade}>{periodicidade}</option>
                      )}
                    </select>
                  </div>

                  <div>
                    <label htmlFor="estado" className={labelClass} style={labelStyle}>
                      Estado
                    </label>
                    <input
                      id="estado"
                      type="text"
                      value={estado}
                      onChange={(event) => setEstado(event.target.value)}
                      className={inputClass}
                      style={inputStyle}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-6">
                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <div>
                    <h3
                      className="text-foreground"
                      style={{
                        fontSize: "var(--text-section-title)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Plano de amortização
                    </h3>
                    <p
                      className="text-secondary-text mt-1"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Capital, taxa e nº de prestações calculam automaticamente a
                      prestação devida e o saldo em dívida (método francês).
                      Opcional.
                    </p>
                  </div>

                  <div>
                    <label htmlFor="capital" className={labelClass} style={labelStyle}>
                      Capital inicial (€)
                    </label>
                    <input
                      id="capital"
                      type="number"
                      min="0"
                      step="0.01"
                      value={capital}
                      onChange={(event) => setCapital(event.target.value)}
                      className={inputClass}
                      style={inputStyle}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label htmlFor="taxaAnual" className={labelClass} style={labelStyle}>
                      Taxa anual TAN (%)
                    </label>
                    <input
                      id="taxaAnual"
                      type="number"
                      min="0"
                      step="0.01"
                      value={taxaAnual}
                      onChange={(event) => setTaxaAnual(event.target.value)}
                      className={inputClass}
                      style={inputStyle}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="numeroPrestacoes"
                      className={labelClass}
                      style={labelStyle}
                    >
                      Nº de prestações
                    </label>
                    <input
                      id="numeroPrestacoes"
                      type="number"
                      min="1"
                      step="1"
                      value={numeroPrestacoes}
                      onChange={(event) =>
                        setNumeroPrestacoes(event.target.value)
                      }
                      className={inputClass}
                      style={inputStyle}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="primeiraPrestacao"
                      className={labelClass}
                      style={labelStyle}
                    >
                      Data da 1ª prestação
                    </label>
                    <input
                      id="primeiraPrestacao"
                      type="date"
                      value={primeiraPrestacao}
                      onChange={(event) =>
                        setPrimeiraPrestacao(event.target.value)
                      }
                      className={inputClass}
                      style={inputStyle}
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div
                  className="bg-card border border-border p-6 space-y-5"
                  style={{ borderRadius: "var(--radius-card)" }}
                >
                  <div>
                    <h3
                      className="text-foreground"
                      style={{
                        fontSize: "var(--text-section-title)",
                        fontWeight: "var(--font-weight-semi-bold)",
                      }}
                    >
                      Empréstimo simples
                    </h3>
                    <p
                      className="text-secondary-text mt-1"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Prestação e vencimento fixos (empréstimo sem plano de
                      amortização).
                    </p>
                  </div>

                  <div>
                    <label htmlFor="prestacao" className={labelClass} style={labelStyle}>
                      Valor / Prestação (€)
                    </label>
                    <input
                      id="prestacao"
                      type="number"
                      min="0"
                      step="0.01"
                      value={prestacao}
                      onChange={(event) => setPrestacao(event.target.value)}
                      className={inputClass}
                      style={inputStyle}
                      disabled={isLoading}
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="dataVencimento"
                      className={labelClass}
                      style={labelStyle}
                    >
                      Data de Vencimento
                    </label>
                    <input
                      id="dataVencimento"
                      type="date"
                      value={dataVencimento}
                      onChange={(event) =>
                        setDataVencimento(event.target.value)
                      }
                      className={inputClass}
                      style={inputStyle}
                      disabled={isLoading}
                    />
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
